import { Observable, of, Subject } from 'rxjs'
import { catchError, filter, first, takeUntil, timeout } from 'rxjs/operators'
import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core'
import { Title } from '@angular/platform-browser'
import { Store } from '@ngrx/store'
import { AppState } from './core/core.state'
import { configurationOptionSelectors } from './features/configuration-option/configuration-option.selectors'
import { AnalyticsSharedService } from './shared/services/analytics-shared.service'
import { GoogleFontsService } from './shared/services/google-fonts.service'
import { environment } from 'src/environments/environment'
import { CustomStyleService } from './shared/services/custom-style.service'
import { settingsActions } from './core/settings/settings.actions'
import { settingSelectors } from './core/settings/settings.selectors'
import { ConfigurationService } from './shared/services/configuration.service'
import { FeedbackService } from './shared/components/feedback-modal/feedback.service'
import { CookieConsentService } from './shared/components/cookie-consent-banner/cookie-consent.service'
import { AppToWebMessageType, IDeviceInfo } from './shared/interfaces'
import {
  NotificationService,
  NotificationType
} from './core/notification.service'
import { ConfigOptionsKeys } from './shared/constants'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private messageEventListener: (event: MessageEvent) => void
  public selectedLanguage$: Observable<string>
  private destroy$ = new Subject<void>()

  constructor(
    private titleService: Title,
    private store: Store<AppState>,
    private analyticsService: AnalyticsSharedService,
    private googleFontsService: GoogleFontsService,
    private customStyleService: CustomStyleService,
    private configurationService: ConfigurationService,
    private feedbackService: FeedbackService,
    private notificationService: NotificationService,
    private cookieConsentService: CookieConsentService
  ) {
    // Initialize consent settings first (this enables/disables analytics based on stored consent)
    try {
      this.cookieConsentService.initializeFromStoredConsent()

      if (this.cookieConsentService.isAnalyticsEnabled()) {
        this.analyticsService.logEvents('app_open', {
          component: 'AppComponent'
        })
      }
    } catch (error) {
      console.error('Error initializing cookie consent settings:', error)
    }
  }

  private handlePostMessages(
    event: MessageEvent<
      | {
          fcmToken?: string
          deviceId?: string
          appLaunches?: string
          type: AppToWebMessageType.APP_INFO
        }
      | {
          translationKey: string
          notificationType: NotificationType
          type: AppToWebMessageType.APP_NOTIFICATION
        }
      | (IDeviceInfo & {
          type: AppToWebMessageType.APP_DEVICE_INFO
        })
    >
  ): void {
    if (event.origin !== window.origin) return

    const data = event.data

    switch (data.type) {
      case AppToWebMessageType.APP_INFO:
        const { type: _, ...appInfoProperties } = data

        this.store.dispatch(
          settingsActions.setMultipleSettings(appInfoProperties)
        )
        break
      case AppToWebMessageType.APP_NOTIFICATION:
        const { translationKey, notificationType } = data

        this.notificationService.emit(translationKey, notificationType)
        break
      case AppToWebMessageType.APP_DEVICE_INFO:
        const { type: __, ...deviceInfo } = data
        this.store.dispatch(
          settingsActions.setOneById({
            id: 'deviceInfo',
            value: JSON.stringify(deviceInfo)
          })
        )
        break
    }
  }

  private requestInfoToTheAPP(): void {
    this.configurationService.sendEventToAPP({
      eventType: 'REQUEST_AVAILABLE_INFO'
    })
  }

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.selectedLanguage$
      .pipe(
        filter((x) => Boolean(x)),
        catchError(() => of(null))
      ) // In case the configuration option is not loaded within 5 seconds, we catch the error and return null to prevent the app from breaking)
      .subscribe((language) => {
        document.documentElement.lang = language || 'en'
      })

    this.setupEventListener()
    this.saveIsMobileApp()
    this.requestInfoToTheAPP()

    this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.CUSTOM_STYLE_SETTINGS
      )
      .pipe(
        first((webOption) => Boolean(webOption?.value)),
        timeout(5000),
        catchError(() => of(null)) // In case the configuration option is not loaded within 5 seconds, we catch the error and return null to prevent the app from breaking
      )
      .subscribe((webOption) => {
        if (webOption?.value?.length) {
          this.customStyleService.setCustomStyle(JSON.parse(webOption.value))
        }
      })

    this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.BROWSER_TITLE
      )
      .pipe(
        first((webOption) => Boolean(webOption)),
        timeout(5000),
        catchError(() => of(null)) // In case the configuration option is not loaded within 5 seconds, we catch the error and return null to prevent the app from breaking
      )
      .subscribe((webOption) => {
        if (webOption?.value) {
          this.titleService.setTitle(webOption.value)
        }
      })

    if (environment.branding.useCustomFonts) {
      this.store
        .select(
          configurationOptionSelectors.selectByKey,
          ConfigOptionsKeys.CUSTOM_FONT_FAMILY
        )
        .pipe(
          first((webOption) => Boolean(webOption?.value)),
          timeout(5000),
          catchError(() => of(null)) // In case the configuration option is not loaded within 5 seconds, we catch the error and return null to prevent the app from breaking
        )
        .subscribe((webOption) => {
          const fontFamilyName = webOption?.value?.length
            ? webOption.value
            : 'Open Sans'

          this.googleFontsService.addGoogleFontLink(fontFamilyName)
          this.googleFontsService.useGoogleFont(fontFamilyName)
        })
    }

    this.store
      .select(settingSelectors.selectById, 'appLaunches')
      .pipe(
        filter((value) => parseInt(value) === 100),
        catchError(() => of(null)), // In case the configuration option is not loaded within 5 seconds, we catch the error and return null to prevent the app from breaking
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.feedbackService.triggerFeedbackAfterSuccessfulAction()
      })
  }

  setupEventListener() {
    this.messageEventListener = (event) => this.handlePostMessages(event)
    window.addEventListener('message', this.messageEventListener)
  }

  saveIsMobileApp() {
    this.store.dispatch(
      settingsActions.setOneById({
        id: 'isMobileApp',
        value: String(navigator.userAgent.includes('scan-app'))
      })
    )
  }

  ngOnDestroy() {
    if (this.messageEventListener) {
      window.removeEventListener('message', this.messageEventListener)
    }
    this.destroy$.next()
    this.destroy$.complete()
  }
}
